import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Building2 } from 'lucide-react';
import '../../../shared/StyledInput.css';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import RecordLookupField from '../../../shared/RecordLookupField';
import ToggleSwitch from '../../../shared/ToggleSwitch';
import NumberInput from '../../../shared/NumberInput';
import { mediaFileUrl } from '../../../shared/mediaFileUrl';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { SegmentedToggle } from '../../../shared/DashboardChart';
import { isThisBusiness, ownerName, ownerHref } from '../../../shared/brandLabel';

// Only active Organisations can be picked as an Owner; a Brand keeps an
// Owner that has since been deactivated.
const searchOwners = (term) => axios
  .get('/api/commercial-entities/quick-search', { params: { q: term, type: 'organization' } })
  .then((res) => res.data.data || []);

// Who owns the Brand (#430): the business itself, or an Organisation.
const OWNER_KINDS = [
  { key: 'business', label: 'This business' },
  { key: 'organisation', label: 'An Organisation' },
];

// Only a We distribute Brand can be shown on the website (issue #285).
const DISTRIBUTE_KEY = 'distribute';

// A web address suggested from the Brand's name: "G&H Plugs" → "g-h-plugs".
function slugFrom(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 100);
}

// The Brand's logo for the website, kept in the Media Library as a Brand
// Logo. Uploading or attaching only sets the form's value; it is saved with
// the Brand.
function BrandLogoField({ logoId, onChange }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [uploading, setUploading] = useState(false);
  const [existing, setExisting] = useState([]);

  useEffect(() => {
    axios.get('/api/media-images', { params: { category: 'brand_logo' } })
      .then((res) => setExisting(res.data.data || []))
      .catch(() => setExisting([]));
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post('/api/brands/logo', formData);
      onChange(res.data.data.logo.id);
      dismissError();
    } catch (err) {
      showError(err, 'Could not upload logo');
    }
    setUploading(false);
  };

  const others = existing.filter((img) => String(img.id) !== String(logoId));

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <div style={{ width: 140, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6 }}>
        {logoId
          ? <img src={mediaFileUrl(logoId)} alt="Brand logo" style={{ maxWidth: 128, maxHeight: 48, objectFit: 'contain' }} />
          : <span style={{ fontSize: 12, color: '#9aa3ae' }}>No logo</span>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label style={{ fontSize: 13, color: '#1976d2', fontWeight: 500, cursor: uploading ? 'not-allowed' : 'pointer' }}>
          {uploading ? 'Uploading...' : logoId ? 'Replace logo' : 'Upload logo'}
          <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={handleFileChange} disabled={uploading} style={{ display: 'none' }} />
        </label>
        {logoId && (
          <button type="button" onClick={() => onChange(null)} disabled={uploading} style={{ background: 'none', border: 'none', color: '#ff6f61', cursor: 'pointer', textDecoration: 'underline', fontSize: 12, padding: 0, textAlign: 'left' }}>
            Remove logo
          </button>
        )}
        {others.length > 0 && (
          <select className="styled-input" value="" onChange={(e) => e.target.value && onChange(Number(e.target.value))} disabled={uploading} style={{ fontSize: 13 }}>
            <option value="">Attach existing...</option>
            {others.map((img) => <option key={img.id} value={img.id}>{img.original_filename}</option>)}
          </select>
        )}
        <div style={{ fontSize: 11, color: '#888' }}>PNG, JPEG, WebP or SVG</div>
      </div>
    </div>
  );
}

// initialOwner pre-sets the Owner when the form opens from that
// Organisation's own page, or from Company Information for the business.
function BrandForm({ initial, initialOwner, onSave, onCancel, loading, origin }) {
  const startOwner = initial?.owner || initialOwner;
  const startsWithBusiness = isThisBusiness(startOwner);
  const [ownerKind, setOwnerKind] = useState(startsWithBusiness ? 'business' : 'organisation');
  const [business, setBusiness] = useState(startsWithBusiness ? startOwner : null);
  // The Organisation picked, kept while the business is chosen so switching
  // back restores it.
  const [orgOwner, setOrgOwner] = useState(startOwner && !startsWithBusiness ? { id: startOwner.id, label: startOwner.name } : null);
  const [name, setName] = useState(initial?.name || '');
  const [brandTypeId, setBrandTypeId] = useState(initial?.brand_type_id ? String(initial.brand_type_id) : '');
  const [brandTypes, setBrandTypes] = useState([]);
  const [description, setDescription] = useState(initial?.description || '');
  const [url, setUrl] = useState(initial?.url || '');
  const [showOnWebsite, setShowOnWebsite] = useState(!!initial?.show_on_website);
  const [logoId, setLogoId] = useState(initial?.logo_media_image_id || null);
  const [webSlug, setWebSlug] = useState(initial?.web_slug || '');
  const [displayOrder, setDisplayOrder] = useState(initial?.display_order ?? '');
  const [error, setError] = useState('');

  // Active types, plus this Brand's own type if it has since been deactivated.
  useEffect(() => {
    axios.get('/api/brand-types', { params: { status: 'all' } })
      .then((res) => setBrandTypes(res.data.data || []))
      .catch(() => setBrandTypes([]));
  }, []);
  useEffect(() => {
    axios.get('/api/brands/this-business')
      .then((res) => setBusiness(res.data.data || null))
      .catch(() => {});
  }, []);
  const owner = ownerKind === 'business' ? business : orgOwner;
  const typeOptions = brandTypes.filter((t) => t.is_active || String(t.id) === brandTypeId);
  const selectedType = brandTypes.find((t) => String(t.id) === brandTypeId);
  const distributed = selectedType?.system_key === DISTRIBUTE_KEY;

  const handleShowChange = (value) => {
    setShowOnWebsite(value);
    if (value && !webSlug.trim()) setWebSlug(slugFrom(name));
  };

  const validate = () => {
    if (!owner) return 'Owner is required.';
    if (!name.trim()) return 'Name is required.';
    if (!brandTypeId) return 'Brand Type is required.';
    if (distributed && showOnWebsite) {
      const missing = [!logoId && 'a logo', !description.trim() && 'a description', !webSlug.trim() && 'a web address'].filter(Boolean);
      if (missing.length) return `Add ${missing.join(', ').replace(/, ([^,]*)$/, ' and $1')} to show this Brand on the website.`;
    }
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    setError('');
    onSave({
      owner_id: owner.id,
      name: name.trim(),
      brand_type_id: Number(brandTypeId),
      description: description.trim(),
      url: url.trim(),
      show_on_website: distributed && showOnWebsite,
      logo_media_image_id: logoId,
      web_slug: webSlug.trim(),
      display_order: displayOrder === '' ? null : displayOrder,
    });
  };

  return (
    <form className="brand-form" onSubmit={handleSubmit} style={{ minWidth: 320 }}>
      <div>
        <label>Owner<RequiredMark /></label><br />
        <div style={{ marginBottom: 8 }}>
          <SegmentedToggle options={OWNER_KINDS} value={ownerKind} onChange={setOwnerKind} />
        </div>
        {ownerKind === 'business' ? (
          <RecordLookupField
            value={business ? { id: business.id, label: ownerName(business) } : null}
            href={business ? ownerHref(business) : undefined}
            icon={Building2}
            origin={origin}
            openTitle="Open Company Information"
            readOnly
            width={280}
          />
        ) : (
          <RecordLookupField
            value={orgOwner}
            search={searchOwners}
            onSelect={(r) => setOrgOwner({ id: r.id, label: r.name })}
            href={orgOwner ? `/commercial-entities/${orgOwner.id}` : undefined}
            icon={Building2}
            placeholder="Search Organisations..."
            origin={origin}
            openTitle="Open owner"
            changeTitle="Change owner"
            width={280}
          />
        )}
      </div>

      <div style={{ marginTop: 14 }}>
        <label>Name<RequiredMark /></label><br />
        <input
          className="styled-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          placeholder="e.g., SP Cables"
          style={{ width: 280 }}
        />
      </div>

      <div style={{ marginTop: 14 }}>
        <label>Brand Type<RequiredMark /></label><br />
        <select className="styled-input" value={brandTypeId} onChange={(e) => setBrandTypeId(e.target.value)} style={{ width: 280 }}>
          <option value="">Select a Brand Type...</option>
          {typeOptions.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      <div style={{ marginTop: 14 }}>
        <label>URL</label><br />
        <input
          className="styled-input"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          maxLength={500}
          placeholder="e.g., spcables.com.au"
          style={{ width: '100%', maxWidth: 480 }}
        />
      </div>

      <div style={{ marginTop: 14 }}>
        <label>Description</label><br />
        <textarea
          className="styled-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={6}
          style={{ width: '100%', maxWidth: 480, resize: 'vertical' }}
          placeholder={distributed ? 'What the brand makes, as shown on the website' : 'Optional notes'}
        />
      </div>

      {distributed && (
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #e3e7ee', maxWidth: 480 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>Website</div>
          <ToggleSwitch checked={showOnWebsite} onChange={handleShowChange} label="Show on website" />

          <div style={{ marginTop: 14 }}>
            <label>Logo{showOnWebsite && <RequiredMark />}</label><br />
            <BrandLogoField logoId={logoId} onChange={setLogoId} />
          </div>

          <div style={{ marginTop: 14 }}>
            <label>Web address{showOnWebsite && <RequiredMark />}</label><br />
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 14, color: '#666' }}>/brands/</span>
              <input
                className="styled-input"
                value={webSlug}
                onChange={(e) => setWebSlug(e.target.value.toLowerCase())}
                maxLength={100}
                placeholder="e.g., sp-cables"
                style={{ width: 220 }}
              />
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <label>Display order</label><br />
            <NumberInput
              className="styled-input"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
              decimals={0}
              min="0"
              placeholder="e.g., 1"
              style={{ width: 100 }}
            />
          </div>
        </div>
      )}

      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}

      <div style={{ marginTop: 12 }}>
        <SaveButton loading={loading} />
        <CancelButton onClick={onCancel} disabled={loading} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

export default BrandForm;
